import React, { useEffect, useState, useCallback } from "react";
import {
  Modal,
  Input,
  Form,
  Button,
  Radio,
  List,
  Image,
  Space,
  message,
  Checkbox,
  Switch,
  Alert,
  Divider,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import { Locale, Util } from "../../../utils";
import TranslateBtn from "../../../components/product/TranslateBtn";
import ImageUpload from "../../../components/media/ImageUpload";
import { JD_ID } from "../../../constants";
import "./HomeProductGroups.scss";

const { Search } = Input;
const util = Util.getInstance();
const MAX_ITEMS = 50;

function getImagePath(value) {
  if (value == null || value === "") return null;
  if (typeof value === "string") return value;
  if (Array.isArray(value) && value.length > 0) {
    const first = value[0];
    if (typeof first === "string") return first;
    if (first && (first.url || first.file_path)) return first.url || first.file_path;
  }
  if (value && (value.url || value.file_path)) return value.url || value.file_path;
  return null;
}

/** Returns path only for API submission (strips URL origin if present). */
function toSubmissionPath(value) {
  const raw = getImagePath(value);
  if (raw == null || raw === "") return null;
  if (typeof raw !== "string") return null;
  if (raw.startsWith("http://") || raw.startsWith("https://")) {
    try {
      const pathname = new URL(raw).pathname;
      return pathname.startsWith("/") ? pathname.slice(1) : pathname;
    } catch {
      return raw;
    }
  }
  return raw;
}

function productThumb(item) {
  if (!item) return null;
  if (item.product_image) return item.product_image;
  const img = item.images && item.images[0];
  if (!img) return null;
  if (typeof img === "string") return img;
  return img.url || img.file_path || null;
}

function ProductSelectModal({ visible, onCancel, onSelect, api, token, user, existingProductIds = [] }) {
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchType, setSearchType] = useState("name");
  const [data, setData] = useState({ data: [], current_page: 1, total: 0 });
  const [selectedIds, setSelectedIds] = useState(new Set());

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  useEffect(() => {
    if (!visible) clearSelection();
  }, [visible, clearSelection]);

  const toggleProduct = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const fetchData = useCallback(
    async (page = 1) => {
      if (!token) return;
      let customApi = api.product.products;
      if (user && user.merchant && user.merchant.id === JD_ID) {
        customApi = api.product.jdProducts;
      }
      const query = {
        category_id: -1,
        page,
        keyword,
        search_type: searchType,
        count: 10,
      };
      try {
        setLoading(true);
        const res = await customApi.run({ token, query });
        if (res.result && res.data) {
          setData({
            data: res.data.data || res.data.list || [],
            current_page: page,
            total: res.data.total || (res.data.data && res.data.data.length) || 0,
          });
        }
      } catch (e) {
        message.error(e.message || "Failed to load products");
      } finally {
        setLoading(false);
      }
    },
    [token, keyword, searchType, api, user]
  );

  useEffect(() => {
    if (visible) fetchData(1);
  }, [visible, keyword, searchType, fetchData]);

  const i18n = Locale.getInstance();
  const selectedCount = selectedIds.size;

  const handleAddSelected = () => {
    if (selectedCount === 0) {
      message.warning(i18n.t("homeProductGroups.selectAtLeastOne") || "Please select at least one product");
      return;
    }
    const itemsToAdd = data.data
      .filter((item) => selectedIds.has(item.id))
      .map((item) => ({
        product_id: item.id,
        ug_name: item.ug_name || "",
        zh_name: item.zh_name || item.name || "",
        product_image: productThumb(item),
      }));
    onSelect(itemsToAdd);
    onCancel();
  };

  return (
    <Modal
      className="home-product-groups-select-modal"
      title={i18n.t("homeProductGroups.addProduct")}
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          {i18n.t("proxy.editCancel")}
        </Button>,
        <Button
          key="add"
          type="primary"
          onClick={handleAddSelected}
          disabled={selectedCount === 0}
        >
          {i18n.t("homeProductGroups.addSelectedCount", { count: selectedCount }) || `Add selected (${selectedCount})`}
        </Button>,
      ]}
      width={640}
      destroyOnClose
    >
      <Search
        allowClear
        style={{ marginBottom: 12 }}
        placeholder={i18n.t("product.search.name")}
        value={keyword}
        onChange={(e) => setKeyword(e.target.value)}
        onSearch={() => fetchData(1)}
      />
      <List
        loading={loading}
        dataSource={data.data}
        pagination={{
          current: data.current_page,
          total: data.total,
          pageSize: 10,
          onChange: (page) => fetchData(page),
          size: "small",
        }}
        renderItem={(item) => {
          const checked = selectedIds.has(item.id);
          const alreadyAdded = existingProductIds.includes(item.id);
          const thumb = productThumb(item);
          return (
            <List.Item
              style={{ cursor: alreadyAdded ? "not-allowed" : "pointer", opacity: alreadyAdded ? 0.55 : 1 }}
              onClick={() => !alreadyAdded && toggleProduct(item.id)}
              extra={
                <Checkbox
                  checked={checked}
                  disabled={alreadyAdded}
                  onChange={() => !alreadyAdded && toggleProduct(item.id)}
                  onClick={(e) => e.stopPropagation()}
                />
              }
            >
              <List.Item.Meta
                avatar={
                  thumb ? (
                    <Image src={thumb} width={48} height={48} style={{ objectFit: "cover", borderRadius: 8 }} preview={false} />
                  ) : (
                    <div className="hpg-product-row__thumb-fallback">
                      <AppstoreOutlined />
                    </div>
                  )
                }
                title={item.zh_name || item.ug_name || item.name || `#${item.id}`}
                description={`ID: ${item.id}${alreadyAdded ? ` (${i18n.t("homeProductGroups.alreadyAdded") || "already added"})` : ""}`}
              />
            </List.Item>
          );
        }}
      />
    </Modal>
  );
}

const EditProductGroupForm = ({
  groupData,
  onCancel,
  onFinish,
  loading,
  api,
  getUserToken,
  i18n,
  user,
}) => {
  const [form] = Form.useForm();
  const [productModalVisible, setProductModalVisible] = useState(false);
  const token = getUserToken();

  useEffect(() => {
    if (groupData) {
      const {
        ug_name,
        zh_name,
        ug_title_image,
        zh_title_image,
        list_type,
        is_flashsale,
        items,
      } = groupData;
      form.setFieldsValue({
        ug_name: ug_name ?? "",
        zh_name: zh_name ?? "",
        ug_title_image: util.getDefaultImages(ug_title_image),
        zh_title_image: util.getDefaultImages(zh_title_image),
        list_type: [0, 1, 2].includes(Number(list_type)) ? Number(list_type) : 0,
        is_flashsale: !!is_flashsale,
        items: (items || []).map((it) => ({
          product_id: it.product_id,
          ug_name: it.product_ug_name ?? it.ug_name ?? "",
          zh_name: it.product_zh_name ?? it.zh_name ?? "",
          product_image: it.product_image ?? null,
        })),
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        list_type: 0,
        is_flashsale: false,
        items: [],
      });
    }
  }, [groupData, form]);

  const handleFinish = (values) => {
    const isFlashsale = !!values.is_flashsale;
    const items = isFlashsale
      ? []
      : (values.items || []).map((it) => ({
          product_id: Number(it.product_id),
        }));

    if (!isFlashsale) {
      if (items.length === 0) {
        message.warning(i18n.t("homeProductGroups.itemsRequired"));
        return;
      }
      if (items.length > MAX_ITEMS) {
        message.error(i18n.t("homeProductGroups.maxItemsError"));
        return;
      }
    }

    const payload = {
      ug_name: values.ug_name || null,
      zh_name: values.zh_name || null,
      ug_title_image: toSubmissionPath(values.ug_title_image) || null,
      zh_title_image: toSubmissionPath(values.zh_title_image) || null,
      list_type: [0, 1, 2].includes(Number(values.list_type))
        ? Number(values.list_type)
        : 0,
      is_flashsale: isFlashsale,
      items,
    };
    if (groupData && groupData.id != null) {
      payload.id = groupData.id;
    }
    onFinish(payload);
  };

  const localeInstance = Locale.getInstance();
  const watchedItems = Form.useWatch("items", form) || [];
  const isFlashsale = Form.useWatch("is_flashsale", form);
  const isEdit = groupData && groupData.id != null;

  return (
    <>
      <Modal
        className="home-product-groups-modal"
        width={720}
        title={
          isEdit
            ? i18n.t("homeProductGroups.editGroup")
            : i18n.t("homeProductGroups.addGroup")
        }
        open={!!groupData}
        onCancel={onCancel}
        footer={null}
        forceRender
        destroyOnClose
      >
        <Form
          className="home-product-groups-form"
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          initialValues={{ list_type: 0, is_flashsale: false, items: [] }}
        >
          <div className="hpg-section">
            <div className="hpg-section__head">
              <h4 className="hpg-section__title">{i18n.t("homeProductGroups.basicInfo")}</h4>
            </div>
            <Form.Item name="ug_name" label={localeInstance.t("agentLevel.ug") + " " + localeInstance.t("homeProductGroups.groupTitle")}>
              <Input
                addonAfter={
                  <TranslateBtn
                    context={Form.useWatch("ug_name", form)}
                    onFinish={(value) => form.setFieldsValue({ zh_name: value })}
                    from="ug"
                    to="zh"
                  />
                }
              />
            </Form.Item>
            <Form.Item name="zh_name" label={localeInstance.t("agentLevel.zh") + " " + localeInstance.t("homeProductGroups.groupTitle")}>
              <Input
                addonAfter={
                  <TranslateBtn
                    context={Form.useWatch("zh_name", form)}
                    onFinish={(value) => form.setFieldsValue({ ug_name: value })}
                    from="zh"
                    to="ug"
                  />
                }
              />
            </Form.Item>
          </div>

          <Divider style={{ margin: "4px 0 20px" }} />

          <div className="hpg-section">
            <div className="hpg-section__head">
              <div>
                <h4 className="hpg-section__title">{i18n.t("homeProductGroups.titleImages")}</h4>
              </div>
            </div>
            <div className="hpg-title-grid">
              <Form.Item name="ug_title_image" label={localeInstance.t("sales.ad.ugImg")}>
                <ImageUpload
                  images={Form.useWatch("ug_title_image", form)}
                  setSelectedImages={(image) => form.setFieldsValue({ ug_title_image: image })}
                />
              </Form.Item>
              <Form.Item name="zh_title_image" label={localeInstance.t("sales.ad.zhImg")}>
                <ImageUpload
                  images={Form.useWatch("zh_title_image", form)}
                  setSelectedImages={(image) => form.setFieldsValue({ zh_title_image: image })}
                />
              </Form.Item>
            </div>
          </div>

          <Divider style={{ margin: "4px 0 20px" }} />

          <div className="hpg-section">
            <div className="hpg-section__head">
              <h4 className="hpg-section__title">{i18n.t("homeProductGroups.displaySettings")}</h4>
            </div>
            <Form.Item name="list_type" label={i18n.t("homeProductGroups.listType")}>
              <Radio.Group optionType="button" buttonStyle="solid">
                <Radio.Button value={0}>{i18n.t("homeProductGroups.onePerRow")}</Radio.Button>
                <Radio.Button value={1}>{i18n.t("homeProductGroups.twoPerRow")}</Radio.Button>
                <Radio.Button value={2}>{i18n.t("homeProductGroups.threePerRow")}</Radio.Button>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              name="is_flashsale"
              label={i18n.t("homeProductGroups.flashSaleType")}
              valuePropName="checked"
              extra={i18n.t("homeProductGroups.flashSaleHint")}
            >
              <Switch />
            </Form.Item>
          </div>

          <Divider style={{ margin: "4px 0 20px" }} />

          {isFlashsale ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 20 }}
              message={i18n.t("homeProductGroups.flashSaleAutoProducts")}
            />
          ) : (
            <div className="hpg-section">
              <div className="hpg-product-toolbar">
                <div>
                  <h4 className="hpg-section__title">{i18n.t("homeProductGroups.selectedProducts")}</h4>
                  <p className="hpg-section__desc">
                    {i18n.t("homeProductGroups.productCountValue", {
                      count: watchedItems.length,
                    })}
                  </p>
                </div>
                <Button
                  type="primary"
                  ghost
                  icon={<PlusOutlined />}
                  onClick={() => setProductModalVisible(true)}
                  disabled={watchedItems.length >= MAX_ITEMS}
                >
                  {i18n.t("homeProductGroups.addProduct")}
                </Button>
              </div>

              <Form.List name="items">
                {(fields, { remove }) => (
                  <div className="hpg-product-list">
                    {fields.length === 0 ? (
                      <div style={{ padding: "28px 16px", textAlign: "center", color: "rgba(0,0,0,0.45)" }}>
                        {i18n.t("homeProductGroups.itemsRequired")}
                      </div>
                    ) : (
                      fields.map((field, index) => {
                        const item = watchedItems[index] || watchedItems[field.name];
                        const productId = item && item.product_id;
                        const productName = item && localeInstance.getPropNameByLocale(item, "name");
                        const displayLabel = productName
                          ? productName
                          : `${i18n.t("homeProductGroups.productId")}: ${productId}`;
                        const thumb = item && item.product_image;
                        return (
                          <div className="hpg-product-row" key={field.key}>
                            <Form.Item name={[field.name, "product_id"]} hidden>
                              <Input type="hidden" />
                            </Form.Item>
                            <Form.Item name={[field.name, "ug_name"]} hidden>
                              <Input type="hidden" />
                            </Form.Item>
                            <Form.Item name={[field.name, "zh_name"]} hidden>
                              <Input type="hidden" />
                            </Form.Item>
                            <Form.Item name={[field.name, "product_image"]} hidden>
                              <Input type="hidden" />
                            </Form.Item>
                            {thumb ? (
                              <img className="hpg-product-row__thumb" src={thumb} alt="" />
                            ) : (
                              <div className="hpg-product-row__thumb-fallback">
                                <AppstoreOutlined />
                              </div>
                            )}
                            <div className="hpg-product-row__body">
                              <p className="hpg-product-row__name">{displayLabel}</p>
                              <p className="hpg-product-row__id">#{productId}</p>
                            </div>
                            <Button
                              type="text"
                              danger
                              size="small"
                              icon={<DeleteOutlined />}
                              onClick={() => remove(field.name)}
                            />
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </Form.List>
            </div>
          )}

          <Form.Item style={{ marginTop: 24, marginBottom: 8 }}>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button onClick={onCancel}>{localeInstance.t("proxy.editCancel")}</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {localeInstance.t("sales.submit")}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <ProductSelectModal
        visible={productModalVisible}
        onCancel={() => setProductModalVisible(false)}
        onSelect={(itemsToAdd) => {
          const items = form.getFieldValue("items") || [];
          const remaining = MAX_ITEMS - items.length;
          const toAppend = (Array.isArray(itemsToAdd) ? itemsToAdd : [itemsToAdd])
            .slice(0, remaining)
            .map((item) => ({
              product_id: item.product_id,
              ug_name: item.ug_name ?? "",
              zh_name: item.zh_name ?? "",
              product_image: item.product_image ?? null,
            }));
          form.setFieldsValue({
            items: [...items, ...toAppend],
          });
          if (toAppend.length < (Array.isArray(itemsToAdd) ? itemsToAdd.length : 1)) {
            message.warning(i18n.t("homeProductGroups.maxItemsError"));
          }
          setProductModalVisible(false);
        }}
        existingProductIds={(form.getFieldValue("items") || []).map((it) => it.product_id)}
        api={api}
        token={token}
        user={user}
      />
    </>
  );
};

export default EditProductGroupForm;
